<template>
  <div class="tab-content">
    <div class="app-download-section">
      <div class="hiking-image d-none d-md-block">
        <img :src="hikingImage1" alt="Hikers on trail" />
      </div>

      <div class="export-options">
        <h2 class="display-5"><strong>Output the route as a file</strong></h2>

        <div class="file-format-options">
          <button
            v-if="isUserLoggedIn"
            v-for="format in fileFormats"
            :key="format.id"
            class="format-option"
            @click="handleExportFormat(format.id)"
            :disabled="!!downloadingFormatId" >
            <span>{{ format.name }}</span>
            <span class="d-flex align-items-center">
              <span v-if="downloadingFormatId === format.id" class="spinner"></span>
              <span v-else class="material-icons text-yellow">arrow_circle_down</span>
            </span>
          </button>

          <button
            v-else
            v-for="format in fileFormats"
            class="format-option"
            @click="handleLogin"
          >
            <span>{{ format.name }}</span>
            <span class="d-flex align-items-center">
              <span class="mr-1" style="width: 95px; line-height: 1;"><small class="text-muted" style="font-size: .75em">Sign in to download</small></span>
              <span class="material-icons text-muted">arrow_circle_down</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import hikingImage1 from '../../images/download-modal-2.webp';
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal";

// Props
const props = defineProps<{
  userData: any;
}>();

const emit = defineEmits<{
  close: [];
}>();

// Data
// Use a ref to store the ID of the format currently being downloaded
const downloadingFormatId = ref<string | null>(null);

const fileFormats = [
  { id: 'gpx', name: 'GPX' },
  { id: 'kml', name: 'KML (Google Earth)' },
  { id: 'geojson', name: 'GeoJSON' }
];

const isUserLoggedIn = computed(() => {
  return props.userData != null;
});

// Methods
const getTrailUrl = () => {
  if (window.country_slug && window.region_slug && window.slug) {
    return `/trails/${window.country_slug}/${window.region_slug}/${window.slug}`;
  }
  const pathMatch = window.location.pathname.match(/\/trails\/([^\/]+)\/([^\/]+)\/([^\/]+)/);
  if (pathMatch) {
    return `/trails/${pathMatch[1]}/${pathMatch[2]}/${pathMatch[3]}`;
  }
  return window.location.pathname;
};

const handleLogin = () => {
  if (window.onBoardingVue && window.onBoardingVue.launchFor) {
    window.onBoardingVue.launchFor(OnBoardingGoal.DownloadGPX);
  }
};

const handleExportFormat = (formatId: string) => {
  // Set the ID of the format being downloaded
  downloadingFormatId.value = formatId;

  const trailUrl = getTrailUrl();

  const fileEndpoints: Record<string, string> = {
    'gpx': 'gpx_file',
    'kml': 'kml_file',
    'geojson': 'geojson_file'
  };

  const endpoint = fileEndpoints[formatId] || 'gpx_file';
  const downloadUrl = `${trailUrl}/${endpoint}`;

  // In a real application, you'd want to use a more robust download method
  // that can track the actual download progress or completion.
  // window.location.href immediately triggers the download and might not
  // give you a reliable "download complete" event for the spinner.
  window.location.href = downloadUrl;

  // Simulate download completion after a delay.
  // In a real scenario, you'd clear this based on an actual download event.
  setTimeout(() => {
    downloadingFormatId.value = null; // Clear the downloading state
  }, 2000);
};
</script>

<style scoped lang="scss">
// Your existing styles go here
.tab-content {
  animation: fadeIn 0.3s ease-in-out;
  padding: 2em;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.app-download-section {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.hiking-image {
  flex: 0 0 300px;

  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
}

.export-options {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  h2 {
    font-size: 24px;
    margin-bottom: 32px;
    font-weight: 600;
  }
}

.file-format-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.format-option {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
  }

  .download-icon {
    color: #f9e547;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #f9e547;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .app-download-section {
    flex-direction: column;
  }

  .hiking-image {
    flex: none;
    height: 200px;
  }
}
</style>
